import { create } from 'zustand';

export interface Toast {
  id: number;
  text: string;
  tone: 'info' | 'success' | 'error';
}

/** Dialogs open over the table (the player stays seated): shop, wheel, league, settings, menu screens… */
export type DialogKind =
  | 'shop'
  | 'vip'
  | 'referral'
  | 'send'
  | 'wheel'
  | 'chances'
  | 'league'
  | 'boosters'
  | 'achievements'
  | 'horoscope'
  | 'ratings'
  | 'styling'
  | 'settings'
  | 'friends'
  | 'profile'
  | 'bonus'
  | 'report'
  | 'courting';

/** Panels open in the lower half, over the chat: gifts for a player, bottles, stickers, music orders. */
export type SheetKind = 'player' | 'bottle' | 'stickers' | 'dj';

interface Opened<K> {
  kind: K;
  props?: Record<string, unknown>;
}

/** "Winner … hit the jackpot": a big wheel prize somebody just won. */
export interface Winner {
  name: string;
  photoUrl: string | null;
  prize: string;
}

interface UiStore {
  toasts: Toast[];
  winner: Winner | null;
  showWinner: (winner: Winner) => void;
  sheet: Opened<SheetKind> | null;
  dialog: Opened<DialogKind> | null;
  /** The dialog that was open before the current one (a "Details" opened from the shop goes back to it). */
  previous: Opened<DialogKind> | null;
  toast: (text: string, tone?: Toast['tone']) => void;
  dismiss: (id: number) => void;
  openSheet: (kind: SheetKind, props?: Record<string, unknown>) => void;
  closeSheet: () => void;
  openDialog: (kind: DialogKind, props?: Record<string, unknown>, opts?: { stack?: boolean }) => void;
  closeDialog: () => void;
}

let seq = 0;

/** Until when this player's own wheel is turning: its "big win" banner would spoil the result. */
export const wheelSpin = { until: 0 };

let winnerTimer: ReturnType<typeof setTimeout> | undefined;

export const useUi = create<UiStore>((set) => ({
  toasts: [],
  winner: null,
  showWinner: (winner) => {
    clearTimeout(winnerTimer);
    set({ winner });
    winnerTimer = setTimeout(() => set({ winner: null }), 6_000);
  },
  sheet: null,
  dialog: null,
  previous: null,
  toast: (text, tone = 'info') => {
    const id = ++seq;
    set((s) => ({ toasts: [...s.toasts.slice(-2), { id, text, tone }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 3_500);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  openSheet: (kind, props) => set({ sheet: { kind, props } }),
  closeSheet: () => set({ sheet: null }),
  openDialog: (kind, props, opts) =>
    set((s) => ({ dialog: { kind, props }, previous: opts?.stack ? s.dialog : null })),
  closeDialog: () => set((s) => ({ dialog: s.previous, previous: null })),
}));
